import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { Icon } from '../../icons';
import { Image } from '../../image';

/**
 * SelectedGift — pure presentational atom shown in the Surprise & Delight slot
 * after the customer has picked a gift.
 *
 * Two Figma-driven variants:
 *
 * - `banner` (Figma node 34003-163494): the "Nice choice. Your free gift is
 *   in the cart" confirmation. A warm-white card with a leading gift icon and
 *   a single line of Barlow Semibold 16px copy. No image, no remove control.
 *
 * - `lineItem` (Figma node 33995-160116): the actual gift rendered inside the
 *   cart, styled as a cart row. Product image, name, kilojoules subtext, a
 *   black "Free gift" pill under the meta, a "Free" price label top-right,
 *   and a trash icon to remove the gift. No wrapper card / no border —
 *   sits transparent inside the cart container.
 */

const BANNER_COPY = "That's fire! Your free gift has been added";

// `warmWhite100` in @nandosaus/constants resolves to #F7F1EE, which matches the
// Figma "background-secondary" token used on the banner card.
const BannerWrapper = styled.div`
  align-items: center;
  background: ${props => props.theme.colors.warmWhite100};
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  padding: 16px 20px;
`;

const BannerText = styled.span`
  color: ${props => props.theme.colors.black};
  font-family: 'Barlow', Calibri, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
`;

const LineItemWrapper = styled.div`
  background: transparent;
  box-sizing: border-box;
  display: flex;
  gap: 16px;
  padding: 0;
`;

const ImageWrapper = styled.div`
  border-radius: 4px;
  flex-shrink: 0;
  height: 64px;
  overflow: hidden;
  width: 64px;
`;

const ImagePlaceholder = styled.div`
  background: ${props => props.theme.colors.grey100};
  border-radius: 4px;
  height: 100%;
  width: 100%;
`;

const StyledImage = styled(Image)`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
`;

const Name = styled.p`
  color: ${props => props.theme.colors.black};
  font-family: 'Barlow', Calibri, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`;

const Description = styled.p`
  color: ${props => props.theme.colors.grey600};
  font-family: 'Barlow', Calibri, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
`;

const FreeGiftPill = styled.span`
  align-self: flex-start;
  background: ${props => props.theme.colors.black};
  border-radius: 4px;
  color: #ffffff;
  display: inline-flex;
  font-family: 'Barlow', Calibri, sans-serif;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
  padding: 2px 8px;
`;

const RightColumn = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: space-between;
`;

const FreeLabel = styled.span`
  color: ${props => props.theme.colors.black};
  font-family: 'Barlow', Calibri, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
`;

const IconButton = styled.button`
  appearance: none;
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  padding: 4px;

  &:hover,
  &:focus {
    opacity: 0.7;
    outline: none;
  }
`;

const SelectedGift = ({ variant = 'lineItem', name, image = null, description = null, onRemove }) => {
  if (variant === 'banner') {
    return (
      <BannerWrapper data-testid="surprise-and-delight-selected-gift-banner">
        <Icon name="giftCard" width="24px" height="24px" />
        <BannerText>{BANNER_COPY}</BannerText>
      </BannerWrapper>
    );
  }

  return (
    <LineItemWrapper data-testid="surprise-and-delight-selected-gift">
      <ImageWrapper>
        {image ? (
          <StyledImage src={image} alt={name} />
        ) : (
          <ImagePlaceholder data-testid="surprise-and-delight-selected-gift-image-placeholder" />
        )}
      </ImageWrapper>

      <MetaColumn>
        <Name>{name}</Name>
        {description && <Description>{description}</Description>}
        <FreeGiftPill>Free gift</FreeGiftPill>
      </MetaColumn>

      <RightColumn>
        <FreeLabel>Free</FreeLabel>
        <IconButton
          type="button"
          onClick={onRemove}
          aria-label="Remove free gift"
          data-testid="surprise-and-delight-selected-gift-remove"
        >
          <Icon name="trash" width="20px" height="20px" />
        </IconButton>
      </RightColumn>
    </LineItemWrapper>
  );
};

SelectedGift.propTypes = {
  variant: PropTypes.oneOf(['banner', 'lineItem']),
  name: PropTypes.string.isRequired,
  image: PropTypes.string,
  description: PropTypes.string,
  // Only the lineItem variant has a remove control; banner has no remove action.
  onRemove: PropTypes.func,
};

SelectedGift.defaultProps = {
  variant: 'lineItem',
  image: null,
  description: null,
  onRemove: () => {},
};

export { SelectedGift };
